import debounce from 'lodash/debounce';
import isSameDay from 'date-fns/isSameDay';
import React, { useEffect } from 'react';
import locale from 'date-fns/locale/pt-BR';
import {
  DatePicker,
  PickersDay,
  LocalizationProvider,
} from '@material-ui/pickers';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import DateFnsAdapter from '@material-ui/pickers/adapter/date-fns';
import InputAdornment from '@material-ui/core/InputAdornment';
import CloseIcon from '@material-ui/icons/Close';
import ListItem from '@material-ui/core/ListItem';
import { withStyles } from '@material-ui/core/styles';

import { useSnapshots } from '../context/SnapshotsContext';

const BootstrapInput = withStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    padding: 0,
    height: 43,
    width: '100%',
    '& label.Mui-focused': {
      color: theme.palette.primary.main,
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.secondary.light,
      padding: 0,
      paddingRight: 12,
      height: 43,
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      '& fieldset': {
        borderColor: theme.palette.secondary.main,
      },
    },
  },
}))(TextField);

interface IProps {
  disabled?: boolean;
  onChangeDate?: (value: Date | null) => void;
}

const SnapshotPicker = ({ disabled, onChangeDate }: IProps) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [noSnapFoundMessage, setNoSnapFoundMessage] = React.useState('');
  const [internalValue, setInternalValue] = React.useState<Date | null>(null);
  const { snapshots, selectedSnap, changeSelectedSnap, clearSelectedSnap } = useSnapshots();

  useEffect(() => {
    if (selectedSnap && selectedSnap.id) {
      const value = new Date(
        selectedSnap.year,
        selectedSnap.month - 1,
        selectedSnap.day
      );
      setInternalValue(value);
      onChangeDate && onChangeDate(value);
    }
    setIsLoading(false);
  }, [selectedSnap, onChangeDate]);

  const handleSelectedDate = (date: Date | null) => {
    if (!date) return;
    setInternalValue(date);
    onChangeDate && onChangeDate(date);

    const snapshot = snapshots.find((snap) => {
      const formattedDate = new Date(snap.year, snap.month - 1, snap.day);
      return isSameDay(date, new Date(formattedDate));
    });

    if (snapshot) {
      changeSelectedSnap(snapshot.id);
      setNoSnapFoundMessage('');
    }

    if (!snapshot) {
      setNoSnapFoundMessage('Nenhum histórico encontrado na data selecionada');
    }
  };

  const handleClearData = () => {
    clearSelectedSnap();
    setInternalValue(null);
    onChangeDate && onChangeDate(null);
    setNoSnapFoundMessage('');
  };

  return (
    <ListItem disableGutters>
      {isLoading && 'Carregando...'}
      {!disabled && snapshots && snapshots.length > 0 && (
        <LocalizationProvider dateAdapter={DateFnsAdapter} locale={locale}>
          <DatePicker
            value={internalValue}
            onChange={debounce(handleSelectedDate, 800)}
            disableFuture
            loading={isLoading}
            renderLoading={() => 'Carregando...'}
            inputFormat="dd/MM/yyyy"
            renderDay={(day, _, DayComponentProps) => {
              const snapshot = snapshots.find((snap) =>
                isSameDay(day, new Date(snap.year, snap.month - 1, snap.day))
              );

              if (snapshot) {
                DayComponentProps.disabled = false;
              } else {
                DayComponentProps.disabled = true;
              }

              return <PickersDay {...DayComponentProps} />;
            }}
            renderInput={(props) => (
              <BootstrapInput
                {...props}
                label="Histórico"
                size="small"
                margin="dense"
                helperText={noSnapFoundMessage}
                error={!!noSnapFoundMessage}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  endAdornment: internalValue ? (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        aria-label="Apagar data"
                        size="small"
                        onClick={handleClearData}
                      >
                        <CloseIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : props.InputProps.endAdornment ? (
                    props.InputProps.endAdornment
                  ) : null,
                }}
              />
            )}
          />
        </LocalizationProvider>
      )}

      {snapshots && !snapshots.length && 'Nenhum histórico encontrado'}
    </ListItem>
  );
};

export default SnapshotPicker;
